/* hs-eslint ignored failing-rules */

/* eslint-disable promise/catch-or-return */
'use es6';

import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { NavMarker } from 'react-rhumb';
import { stringify } from 'hub-http/helpers/params';
import UIFlex from 'UIComponents/layout/UIFlex';
import UILoadingSpinner from 'UIComponents/loading/UILoadingSpinner';
import GoogleLoginDao from '../../data/GoogleLoginDao';
import googleLoginNonce from '../../lib/googleLoginNonce';
import HubSpotOnlyAppCookieLogin from 'Nabisco/app/HubSpotOnlyAppCookieLogin';
import GoogleLoginButton from '../google/GoogleLoginButton';
import LoginDao from '../../data/LoginDao';
import TwoFactorAuthStatuses from '../../constants/TwoFactorAuthStatuses';
import SsoRequiredAuthStatuses from '../../constants/SsoRequiredAuthStatuses';
import ConfirmToLoginStatuses from '../../constants/ConfirmToLoginAuthStatuses';
import { setTwoFactorMethod } from '../../actions';
const loginDao = new LoginDao();
const appCookieLogin = new HubSpotOnlyAppCookieLogin({
  loginRequest: loginDao.uiLogin.bind(loginDao),
  passThroughFallback: true
});
import LoginFailureHandlerFactory from '../../lib/LoginFailureHandlerFactory';
const googleLoginDao = new GoogleLoginDao();

class GooglePlatform {
  constructor() {
    this._loadPromiseGoogleIdentityServices = null;
    this._loadTimeoutGoogleIdentityServices = undefined;
  }

  registerGoogleIdentityServicesCallback(resolve, reject) {
    const w = window;
    this._loadTimeoutGoogleIdentityServices = setTimeout(() => {
      reject('Load google-platform script timed out.');
    }, 4000); // 4 seconds

    w.onGoogleLibraryLoad = () => {
      clearTimeout(this._loadTimeoutGoogleIdentityServices);
      resolve(true);
    };
  }

  loadGoogleIdentityServices() {
    if (!this._loadPromiseGoogleIdentityServices) {
      this._loadPromiseGoogleIdentityServices = new Promise((resolve, reject) => {
        if (!window.google) {
          this.registerGoogleIdentityServicesCallback(resolve, reject);
          const script = document.createElement('script');
          script.id = 'gsi-login';
          script.async = true;
          script.defer = true;
          script.src = 'https://accounts.google.com/gsi/client';
          document.body.appendChild(script);
        } else {
          resolve(true);
        }
      });
    }

    return this._loadPromiseGoogleIdentityServices;
  }

}

const GoogleLoginComponent = ({
  loginPortalId,
  loginRedirectUrl,
  triggerReset,
  history
}) => {
  const [googleId, setGoogleId] = useState();
  const [isLoading, setLoading] = useState(true);
  const [isLoaded, setLoaded] = useState(false);
  const [initResponse, setInitResponse] = useState(undefined);
  const credentials = {
    loginPortalId,
    loginRedirectUrl
  };
  const dispatch = useDispatch();

  const googleLoginClick = () => {
    const googleInitPromise = new Promise(res => {
      if (initResponse) {
        res(initResponse);
      } else {
        res(googleLoginDao.initialize(credentials));
      }
    });
    googleInitPromise.then(googleLoginNonce.storeAndRedirect).catch(LoginFailureHandlerFactory.createRavenReporter('Google initialization failed', triggerReset, history.push));
  };

  const twoFactorOrRedirectOnSuccess = responses => {
    const response = responses[0];

    if (response.status === TwoFactorAuthStatuses.TWO_FACTOR_REQUIRED) {
      const {
        token,
        primary,
        secondary,
        backupCodesEnabled
      } = response;
      dispatch(setTwoFactorMethod(token, primary, secondary, backupCodesEnabled));
      const nextPath = `/two-factor?${stringify({
        loginPortalId,
        loginRedirectUrl
      })}`;
      history.push(nextPath);
    } else if (response.status === SsoRequiredAuthStatuses.SSO_LOGIN_REQUIRED) {
      const {
        hubId,
        requiredBy
      } = response;
      const nextPath = `/sso?${stringify({
        loginPortalId: hubId,
        required: true,
        requiredBy,
        loginRedirectUrl
      })}`;
      history.push(nextPath);
    } else {
      appCookieLogin.redirectOnLoginSuccess(responses);
    }
  };

  const confirmToLoginOrGenericError = response => {
    if (response.responseJSON && response.responseJSON.status === ConfirmToLoginStatuses.SUSPICIOUS_USER_MUST_CONFIRM) {
      const nextPath = `/confirm-to-login?${stringify({
        loginRedirectUrl
      })}`;
      history.push(nextPath);
      return Promise.resolve();
    } else {
      return LoginFailureHandlerFactory.create(triggerReset, history.push)(response);
    }
  };

  const handleCredentialResponse = () => {
    window.handleCredentialResponse = response => {
      response.loginPortalId = loginPortalId;
      response.loginRedirectUrl = loginRedirectUrl;
      googleLoginDao.passCredentialResponse(response).then(result => {
        twoFactorOrRedirectOnSuccess(result);
      }).catch(err => {
        confirmToLoginOrGenericError(err);
      });
    };
  };

  const checkIfIframeLoaded = () => {
    setTimeout(() => {
      let iframes = document.getElementsByTagName('iframe');

      if (iframes && iframes.length > 0) {
        let isLoadedProperly = false;

        for (let i = 0; i < iframes.length; i++) {
          if (iframes.item(i).src && iframes.item(i).src.indexOf('google') !== -1) {
            isLoadedProperly = true;
          }
        }

        setLoaded(isLoadedProperly);
      } else {
        setLoaded(false);
        const checkOnInterval = setInterval(() => {
          iframes = document.getElementsByTagName('iframe');

          if (iframes && iframes.length > 0) {
            let isLoadedProperly = false;

            for (let i = 0; i < iframes.length; i++) {
              if (iframes.item(i).src && iframes.item(i).src.indexOf('google') !== -1) {
                isLoadedProperly = true;
              }
            }

            setLoaded(isLoadedProperly);
          } else {
            setLoaded(false);
          }
        }, 1000);
        setTimeout(() => {
          clearInterval(checkOnInterval);
        }, 17000); // 17 seconds
      }
    }, 3000); // 3 seconds
  };

  useEffect(() => {
    googleLoginDao.initialize(credentials).then(response => {
      setInitResponse(initResponse);
      return googleLoginNonce.storeAndReturnGoogleId(response);
    }).then(data => {
      setGoogleId(data.googleId);
    }).then(() => {
      const googlePlatformInst = new GooglePlatform();
      const loadPromise = googlePlatformInst.loadGoogleIdentityServices();
      loadPromise.then(hasAlreadyLoaded => {
        setLoaded(hasAlreadyLoaded);
      }).catch(() => {
        setLoaded(false);
        setInitResponse(undefined);
      }).finally(() => {
        setLoading(false);
      });
    }).then(() => {
      checkIfIframeLoaded();
      handleCredentialResponse();
    }).catch(() => {
      setLoaded(false);
      setInitResponse(undefined);
      setLoading(false);
    }); // eslint-disable-next-line
  }, []);
  return /*#__PURE__*/_jsxs(NavMarker, {
    name: "GOOGLE_LOGIN_LOAD",
    children: [/*#__PURE__*/_jsx("div", {
      className: "g_id_signin",
      "data-type": "standard",
      "data-width": "400",
      style: {
        maxHeight: '40px',
        marginBottom: '14px'
      }
    }), /*#__PURE__*/_jsxs(UIFlex, {
      direction: "column",
      align: "center",
      children: [isLoading && /*#__PURE__*/_jsx(UILoadingSpinner, {}), !isLoaded && !isLoading && /*#__PURE__*/_jsx(GoogleLoginButton, {
        onClick: googleLoginClick,
        smartLogin: true
      }), /*#__PURE__*/_jsx("div", {
        id: "g_id_onload",
        "data-client_id": googleId,
        "data-callback": "handleCredentialResponse",
        "data-auto_prompt": "false",
        "data-cancel_on_tap_outside": "false"
      })]
    })]
  });
};

export default GoogleLoginComponent;